import {
  PropsWithChildren,
  RefObject,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

import { Stack } from "@mui/material"
import { useTranslation } from "react-i18next"

import {
  EditButton,
  ExpandedContent,
  ItemsSection,
  LeftContent,
  StyledButtonBase,
  StyledWrapper,
  TitleSection,
} from "./components"

type ProductSearchInputFieldProps = {
  title?: string
  titleLoading?: boolean
  items?: string | string[]
  itemsLoading?: boolean
  searchButtonText: string
  focusRef?: RefObject<HTMLElement | null>
  onSearch?: () => void
}

const ProductSearchInputField = ({
  title,
  titleLoading = false,
  items,
  itemsLoading = false,
  searchButtonText,
  focusRef,
  onSearch,
  children,
}: PropsWithChildren<ProductSearchInputFieldProps>) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const contentId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const previousOpenRef = useRef<boolean>(false)

  const normalizeItemsArray = (items: string | string[]): string[] => {
    return Array.isArray(items) ? items : [items]
  }

  const handleToggle = () => {
    setOpen(!open)
  }

  const getAccessibilityText = () => {
    const parts = [t("editButtonText")]

    if (title && !titleLoading) {
      parts.push(title)
    }

    if (items && !itemsLoading) {
      const itemsArray = normalizeItemsArray(items)
      parts.push(...itemsArray)
    }

    return parts.join(", ")
  }

  useLayoutEffect(() => {
    if (open && focusRef?.current) {
      focusRef.current.focus()
    } else if (!open && previousOpenRef.current && triggerRef.current) {
      // Only focus trigger when actually closing (was previously open)
      triggerRef.current.focus()
    }
    // Update previous open state after handling focus
    previousOpenRef.current = open
  }, [open, focusRef])

  return (
    <StyledWrapper open={open}>
      <StyledButtonBase
        ref={triggerRef}
        onClick={handleToggle}
        sx={{ display: open ? "none" : "flex" }}
        aria-expanded={open}
        aria-controls={contentId}
        aria-label={getAccessibilityText()}
        role="button"
      >
        <LeftContent>
          <TitleSection
            title={title}
            titleLoading={titleLoading}
            contentId={contentId}
          />

          <ItemsSection items={items} itemsLoading={itemsLoading} />
        </LeftContent>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <EditButton
            isLoading={titleLoading || itemsLoading}
            editButtonText={t("editButtonText")}
          />
        </Stack>
      </StyledButtonBase>

      <ExpandedContent
        open={open}
        contentId={contentId}
        searchButtonText={searchButtonText}
        onToggle={handleToggle}
        onSearch={onSearch}
      >
        {children}
      </ExpandedContent>
    </StyledWrapper>
  )
}

export default ProductSearchInputField
